import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Box, Checkbox, FormHelperText, MenuItem, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import ModalShell from './ModalShell';
import { useLoader } from '../../../Utils/ContextHelper/LoaderContext';
import { EMPLOYEE_COUNT_OPTIONS, HOW_DID_YOU_HEAR_OPTIONS } from '../../V2/InnerPages/AssessmentService/data';
import chevronDownIcon from '../../../Assets/Images/v3/icon-chevron-down.svg';
import { trackDemoFunnel } from '../../../Utils/analytics';
import {
	DEMO_VARIANTS,
	DemoField,
	DemoVariant,
	EMPTY_DEMO_FORM,
	PRODUCT_INTEREST,
	validateDemoForm,
	validateField
} from './demoRequestForm';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

const fieldSx = {
	'& .MuiOutlinedInput-root': {
		fontFamily: 'DM Mono',
		fontSize: '16px',
		letterSpacing: '-0.32px',
		color: '#292929',
		borderRadius: '4px',
		backgroundColor: '#ffffff',
		height: '53px',
		'& fieldset': { borderWidth: '2px', borderColor: '#b6dbfe' },
		'&:hover fieldset': { borderColor: '#b6dbfe' },
		'&.Mui-focused fieldset': { borderColor: '#0073de' }
	},
	'& .MuiInputLabel-root': { display: 'none' },
	'& .MuiOutlinedInput-input': {
		padding: '17px 20px',
		'&::placeholder': { color: '#969696', opacity: 1 }
	},
	'& .MuiSelect-select': { padding: '17px 20px !important' },
	'& .MuiFormHelperText-root': { fontFamily: 'DM Mono', color: '#d32f2f', marginLeft: 0 }
};

export interface DemoRequestFormContentProps {
	variant: DemoVariant;
	/** Overall dialog open state — used only to reset form state once the dialog fully closes. */
	open: boolean;
}

export const DemoRequestFormContent = ({ variant, open }: DemoRequestFormContentProps) => {
	const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL ?? '';
	const { showLoader, hideLoader } = useLoader();
	const config = DEMO_VARIANTS[variant];
	const has = (field: DemoField) => config.fields.includes(field);

	const [form, setForm] = useState(EMPTY_DEMO_FORM);
	const [errors, setErrors] = useState<Record<string, string | undefined>>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});
	const [status, setStatus] = useState<SubmitStatus>('idle');
	const [submitError, setSubmitError] = useState('');
	const [companyFax, setCompanyFax] = useState('');

	useEffect(() => {
		if (open) return;
		const timer = setTimeout(() => {
			setForm(EMPTY_DEMO_FORM);
			setErrors({});
			setTouched({});
			setStatus('idle');
			setSubmitError('');
			setCompanyFax('');
		}, 200);
		return () => clearTimeout(timer);
	}, [open]);

	useEffect(() => {
		trackDemoFunnel('form open', { product_interest: PRODUCT_INTEREST[variant], source_page: window.location.href });
	}, []);

	const updateField = (field: DemoField) => (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setForm(prev => ({ ...prev, [field]: value }));
		if (touched[field]) {
			setErrors(prev => ({ ...prev, [field]: validateField(field, { ...form, [field]: value }) }));
		}
	};

	const handleBlur = (field: DemoField) => () => {
		setTouched(prev => ({ ...prev, [field]: true }));
		setErrors(prev => ({ ...prev, [field]: validateField(field, form) }));
	};

	const updateConsent = (event: React.ChangeEvent<HTMLInputElement>) => {
		const checked = event.target.checked;
		setForm(prev => ({ ...prev, consent: checked }));
		setErrors(prev => ({ ...prev, consent: checked ? undefined : prev.consent }));
	};

	const isValid = Object.keys(validateDemoForm(form, config.fields)).length === 0;

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const validationErrors = validateDemoForm(form, config.fields);
		setErrors(validationErrors);
		setTouched(Object.fromEntries(config.fields.map(field => [field, true])));
		if (Object.keys(validationErrors).length > 0) {
			return;
		}

		const productInterest = PRODUCT_INTEREST[variant];
		const sourcePage = window.location.href;

		setSubmitError('');
		setStatus('loading');
		showLoader();
		trackDemoFunnel('submit attempt', { product_interest: productInterest, source_page: sourcePage });
		try {
			const payload: Record<string, string> = {
				product_interest: productInterest,
				source_page: sourcePage,
				company_fax: companyFax
			};
			config.fields.forEach(field => {
				payload[field] = form[field];
			});
			const res = await axios.post(`${baseUrl}/api/contact/demo-request`, payload);
			if (res.data?.success) {
				trackDemoFunnel('submit success', { product_interest: productInterest, source_page: sourcePage });
				setStatus('success');
			} else {
				trackDemoFunnel('submit failure', { product_interest: productInterest, source_page: sourcePage });
				setStatus('error');
				setSubmitError('Something went wrong. Please try again.');
			}
		} catch (err) {
			trackDemoFunnel('submit failure', { product_interest: productInterest, source_page: sourcePage });
			const data = axios.isAxiosError(err) ? err.response?.data : undefined;
			if (data?.errors) {
				setErrors(data.errors);
				setTouched(Object.fromEntries(config.fields.map(field => [field, true])));
				setStatus('idle');
			} else {
				setStatus('error');
				setSubmitError(data?.message || 'Something went wrong. Please try again.');
			}
		} finally {
			hideLoader();
		}
	};

	const renderField = (field: DemoField, placeholder: string) => (
		<TextField
			placeholder={placeholder}
			value={form[field]}
			onChange={updateField(field)}
			onBlur={handleBlur(field)}
			error={Boolean(touched[field] && errors[field])}
			helperText={touched[field] ? errors[field] : undefined}
			disabled={status === 'loading'}
			fullWidth
			sx={fieldSx}
			inputProps={{ 'aria-invalid': Boolean(touched[field] && errors[field]) }}
		/>
	);

	if (status === 'success') {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', paddingTop: '4px' }}>
				<Typography
					component="h2"
					sx={{
						margin: 0,
						fontFamily: 'DM Sans',
						fontWeight: 600,
						fontSize: '26px',
						lineHeight: 1.25,
						letterSpacing: '-0.78px',
						color: '#292929'
					}}
				>
					You&apos;re all set!
				</Typography>
				<Typography
					sx={{
						margin: 0,
						fontFamily: 'DM Mono',
						fontSize: '16px',
						letterSpacing: '-0.32px',
						color: '#6c6c6c',
						lineHeight: 1.5
					}}
				>
					We&apos;ve received your details. Our team will be in touch within 1 business day.
				</Typography>
			</Box>
		);
	}

	return (
		<>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
				<Box
					sx={{
						display: 'inline-flex',
						alignSelf: 'flex-start',
						alignItems: 'center',
						justifyContent: 'center',
						padding: '4px 6px',
						backgroundColor: '#e2edf8',
						borderRadius: '3px'
					}}
				>
					<Typography
						sx={{
							fontFamily: 'DM Mono',
							fontWeight: 400,
							fontSize: '14px',
							lineHeight: 1.5,
							letterSpacing: '-0.28px',
							color: '#075ba9',
							opacity: 0.9
						}}
					>
						{config.eyebrow}
					</Typography>
				</Box>
				<Typography
					component="h2"
					sx={{
						margin: 0,
						fontFamily: 'DM Sans',
						fontWeight: 600,
						fontSize: '26px',
						lineHeight: 1.25,
						letterSpacing: '-0.78px',
						color: '#292929'
					}}
				>
					{config.heading}
				</Typography>
				<Typography
					sx={{
						margin: 0,
						fontFamily: 'DM Mono',
						fontWeight: 500,
						fontSize: '14px',
						lineHeight: 1.5,
						letterSpacing: '-0.28px',
						color: '#969696',
						opacity: 0.9
					}}
				>
					{config.subhead}
				</Typography>
			</Box>

			<Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
				<Box
					component="input"
					type="text"
					name="company_fax"
					tabIndex={-1}
					autoComplete="off"
					aria-hidden="true"
					value={companyFax}
					onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCompanyFax(event.target.value)}
					sx={{
						position: 'absolute',
						width: '1px',
						height: '1px',
						padding: 0,
						margin: '-1px',
						border: 0,
						overflow: 'hidden',
						clip: 'rect(0 0 0 0)',
						whiteSpace: 'nowrap'
					}}
				/>

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: { xs: 'column', sm: 'row' },
							gap: { xs: '12px', sm: '13px' },
							width: '100%'
						}}
					>
						{renderField('first_name', 'First name')}
						{renderField('last_name', 'Last name')}
					</Box>

					<Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: '12px', width: '100%' }}>
						{renderField('business_email', 'Business email')}
						{renderField('phone_number', 'Phone number')}
					</Box>

					{has('company_name') && renderField('company_name', 'Company name')}
					{has('company_website') && renderField('company_website', 'Company website')}
					{has('job_title') && renderField('job_title', 'Job title')}

					{has('employee_count') && (
						<TextField
							select
							value={form.employee_count}
							onChange={updateField('employee_count')}
							onBlur={handleBlur('employee_count')}
							error={Boolean(touched.employee_count && errors.employee_count)}
							helperText={touched.employee_count ? errors.employee_count : undefined}
							disabled={status === 'loading'}
							fullWidth
							sx={fieldSx}
							SelectProps={{
								displayEmpty: true,
								IconComponent: () => (
									<Box
										component="img"
										src={chevronDownIcon}
										alt=""
										sx={{ width: '14px', height: '8px', marginRight: '20px', pointerEvents: 'none' }}
									/>
								)
							}}
						>
							<MenuItem value="" disabled sx={{ fontFamily: 'DM Mono', color: '#969696' }}>
								Employee count
							</MenuItem>
							{EMPLOYEE_COUNT_OPTIONS.map(option => (
								<MenuItem key={option} value={option} sx={{ fontFamily: 'DM Mono' }}>
									{option}
								</MenuItem>
							))}
						</TextField>
					)}

					{has('how_did_you_hear') && (
						<TextField
							select
							value={form.how_did_you_hear}
							onChange={updateField('how_did_you_hear')}
							disabled={status === 'loading'}
							fullWidth
							sx={fieldSx}
							SelectProps={{
								displayEmpty: true,
								IconComponent: () => (
									<Box
										component="img"
										src={chevronDownIcon}
										alt=""
										sx={{ width: '14px', height: '8px', marginRight: '20px', pointerEvents: 'none' }}
									/>
								)
							}}
						>
							<MenuItem value="" sx={{ fontFamily: 'DM Mono', color: '#969696' }}>
								How did you hear about us? (optional)
							</MenuItem>
							{HOW_DID_YOU_HEAR_OPTIONS.map(option => (
								<MenuItem key={option} value={option} sx={{ fontFamily: 'DM Mono' }}>
									{option}
								</MenuItem>
							))}
						</TextField>
					)}
				</Box>

				<Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginTop: '24px' }}>
					<Checkbox
						checked={form.consent}
						onChange={updateConsent}
						disabled={status === 'loading'}
						sx={{ padding: 0, color: '#b6dbfe', '&.Mui-checked': { color: '#0073de' } }}
					/>
					<Typography
						sx={{ fontFamily: 'DM Mono', fontSize: '16px', letterSpacing: '-0.32px', color: '#969696', lineHeight: 1.5 }}
					>
						By submitting this form, you agree to the{' '}
						<Box
							component={RouterLink}
							to="/privacy-policy"
							target="_blank"
							rel="noopener noreferrer"
							sx={{ color: '#0073de' }}
						>
							Privacy Policy
						</Box>{' '}
						and{' '}
						<Box
							component={RouterLink}
							to="/terms-of-use"
							target="_blank"
							rel="noopener noreferrer"
							sx={{ color: '#0073de' }}
						>
							Terms of Use.
						</Box>{' '}
						*
					</Typography>
				</Box>
				{errors.consent && (
					<FormHelperText error sx={{ fontFamily: 'DM Mono', marginTop: '4px' }}>
						{errors.consent}
					</FormHelperText>
				)}

				{status === 'error' && submitError && (
					<Typography sx={{ fontFamily: 'DM Mono', fontSize: '13px', color: '#d32f2f', marginTop: '12px' }}>
						{submitError}
					</Typography>
				)}

				<Box
					component="button"
					type="submit"
					disabled={!isValid || status === 'loading'}
					sx={{
						marginTop: '24px',
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						height: '46px',
						padding: '11px 17px',
						boxSizing: 'border-box',
						backgroundColor: '#0073de',
						border: 'none',
						borderRadius: '4px',
						fontFamily: 'DM Mono',
						fontWeight: 500,
						fontSize: '16px',
						letterSpacing: '-0.32px',
						color: '#ffffff',
						cursor: 'pointer',
						'&:disabled': { backgroundColor: '#b6dbfe', cursor: 'not-allowed' }
					}}
				>
					{status === 'loading' ? 'Submitting…' : 'Submit'}
				</Box>
			</Box>
		</>
	);
};

export interface DemoRequestModalProps {
	open: boolean;
	onClose: () => void;
	variant: DemoVariant;
}

const DemoRequestModal = ({ open, onClose, variant }: DemoRequestModalProps) => {
	return (
		<ModalShell open={open} onClose={onClose}>
			<DemoRequestFormContent variant={variant} open={open} />
		</ModalShell>
	);
};

export default DemoRequestModal;
